/**
 * Editor Sidebar
 * 
 * Table of contents navigation for document sections.
 * Allows quick navigation through large documents.
 */

import { Editor } from '@tiptap/react';
import { useState } from 'react';
import { logger } from '../../utils/logger';
import type { AeroDocument, DocumentSection } from '../../types/document-schema';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui';

export interface EditorSidebarProps {
  document: AeroDocument;
  editor: Editor;
  onAddSection?: (sectionType: 'section' | 'subsection' | 'appendix', title: string, parentId?: string) => void;
}

export function EditorSidebar({ document, editor, onAddSection }: EditorSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionType, setNewSectionType] = useState<'section' | 'subsection' | 'appendix'>('section');
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="w-8 h-full bg-muted hover:bg-muted border-r border-border flex items-center justify-center text-muted-foreground transition-colors"
        title="Show Table of Contents"
      >
        ▶
      </button>
    );
  }
  
  return (
    <div className="w-64 bg-muted border-r border-border flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0">
        <div className="p-4 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Table of Contents</h3>
              <div className="text-xs text-muted-foreground mt-0.5">
                {document.body.length} sections • Modified {new Date(document.lastModified).toLocaleDateString()}
              </div>
            </div>
            <button
              onClick={() => setCollapsed(true)}
              className="text-muted-foreground hover:text-foreground text-sm"
              title="Hide sidebar"
            >
              ◀
            </button>
          </div>
        </div>
        
        {/* Add Section Buttons */}
        {onAddSection && (
          <div className="flex gap-2 px-4 pb-4">
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                setNewSectionType('section');
                setSelectedParentId('');
                setNewSectionTitle('');
                setShowAddSectionModal(true);
              }}
              title="Add new top-level section"
            >
              + Section
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                setNewSectionType('subsection');
                setSelectedParentId('');
                setNewSectionTitle('');
                setShowAddSectionModal(true);
              }}
              title="Add new subsection"
            >
              + Subsection
            </Button>
          </div>
        )}
        
        {/* Divider line */}
        <div className="border-b border-border"></div>
      </div>
      
      {/* TOC - scrollable section */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <nav>
          <ul className="space-y-1">
            {document.body
              .filter(section => !section.parent) // Only show top-level sections
              .map(section => (
                <SectionTocItem
                  key={section.id}
                  section={section}
                  allSections={document.body}
                  level={0}
                  editor={editor}
                />
              ))}
          </ul>
        </nav>
      </div>
      
      {/* Add Section Modal */}
      <Dialog open={showAddSectionModal} onOpenChange={setShowAddSectionModal}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              Add {newSectionType === 'section' ? 'Section' : newSectionType === 'subsection' ? 'Subsection' : 'Appendix'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter section title..."
              autoFocus
            />
          </div>
          
          {newSectionType === 'subsection' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Parent Section
              </label>
              <select
                value={selectedParentId}
                onChange={(e) => setSelectedParentId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select parent section...</option>
                {document.body
                  .filter(s => s.type === 'section')
                  .map(section => (
                    <option key={section.id} value={section.id}>
                      {section.number} {section.title}
                    </option>
                  ))}
              </select>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShowAddSectionModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newSectionTitle.trim() && onAddSection) {
                  onAddSection(newSectionType, newSectionTitle, selectedParentId || undefined);
                  setShowAddSectionModal(false);
                  setNewSectionTitle('');
                  setSelectedParentId('');
                }
              }}
              disabled={!newSectionTitle.trim()}
            >
              Add {newSectionType === 'section' ? 'Section' : newSectionType === 'subsection' ? 'Subsection' : 'Appendix'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SectionTocItemProps {
  section: DocumentSection;
  allSections: DocumentSection[];
  level: number;
  editor: Editor;
}

function SectionTocItem({ section, allSections, level, editor }: SectionTocItemProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = (section.children?.length ?? 0) > 0;
  
  const handleClick = () => {
    // Scroll to section in editor
    logger.debug('[TOC] Clicking section:', section.id, section.title);
    editor.commands.focus();
    
    // Find the heading with this section ID
    const { state, view } = editor;
    const { doc } = state;
    
    let foundPos: number | null = null;
    
    doc.descendants((node, pos) => {
      if (node.type.name === 'heading' && node.attrs.id === section.id) {
        foundPos = pos;
        return false; // Stop iteration
      }
      return true;
    });
    
    if (foundPos !== null) {
      // Find the DOM element for this heading
      const editorElement = view.dom.closest('.overflow-y-auto');
      if (!editorElement) return;
      
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Get DOM node at this position
        const domNode = view.nodeDOM(foundPos);
        
        if (domNode && domNode instanceof HTMLElement) {
          // Calculate scroll position relative to container
          const containerRect = editorElement.getBoundingClientRect();
          const nodeRect = domNode.getBoundingClientRect();
          const scrollTop = editorElement.scrollTop;
          
          // Target: put the heading 100px from top of visible area
          const targetScroll = scrollTop + (nodeRect.top - containerRect.top) - 100;
          
          editorElement.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
          
          // Set selection after scroll starts
          setTimeout(() => editor.commands.setTextSelection(foundPos), 100);
          
          logger.debug('[TOC] Scrolled to section:', section.id);
        } else {
          // Fallback: use position-based scroll
          logger.warn('[TOC] DOM node not found, using position-based scroll');
          editor.commands.setTextSelection(foundPos);
          editor.commands.scrollIntoView();
        }
      });
    } else {
      logger.warn('[TOC] Section heading not found in editor:', section.id);
    }
  };
  
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };
  
  const paddingLeft = `${level * 0.75}rem`;
  
  return (
    <li>
      <div
        className="flex items-center gap-2 py-1 px-2 rounded cursor-pointer hover:bg-muted transition-colors group"
        style={{ paddingLeft }}
        onClick={handleClick}
      >
        {hasChildren && (
          <button
            onClick={handleToggle}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
        {!hasChildren && <span className="w-4"></span>}
        
        <span className="flex-1 text-sm text-foreground group-hover:text-foreground truncate">
          <span className="font-medium">{section.number}</span> {section.title}
        </span>
      </div>
      
      {hasChildren && expanded && (
        <ul className="mt-1 space-y-1">
          {section.children.map(childId => {
            const childSection = allSections.find(s => s.id === childId);
            if (!childSection) return null;
            
            return (
              <SectionTocItem
                key={childId}
                section={childSection}
                allSections={allSections}
                level={level + 1}
                editor={editor}
              />
            );
          })}
        </ul>
      )}
    </li>
  );
}

/**
 * Get icon for custom section type
 */
function _getCustomTypeIcon(customType: string): string {
  switch (customType) {
    case 'safety-analysis':
      return '⚠️';
    case 'test-results':
      return '✅';
    case 'requirements':
      return '📋';
    case 'architecture':
      return '🏗️';
    case 'traceability':
      return '🔗';
    case 'verification':
      return '🔍';
    case 'compliance':
      return '✓';
    default:
      return '📄';
  }
}
